import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { trackSignupButtonClicked } from '@/telemetry/posthog-events';

/**
 * Ecranul de alegere de pe `/autentificare` (decizia owner-ului, 21.09): cele doua intrari,
 * «Autentificare» si «Înscriere», UNA SUB ALTA, fiecare cu textul ei in jumatatea stanga
 * („Ai deja cont?”, „Ești profesor nou?”) si butonul in jumatatea dreapta. Fiecare duce la pasul ei cu
 * emailul: `/autentificare/email` (cod pentru un cont existent) si `/cod-de-acces` (inscriere).
 *
 * ⚠️ Doua LINKURI, nu butoane cu `onClick`: fiecare pas e o ruta proprie, deci «Înapoi» din
 * browser (si butonul de pe telefon) aduce omul aici, pe alegere.
 *
 * 44px inaltime la 360px (`size="lg"` = `h-11` sub `md`).
 *
 * Randata doar cand rutele cu cod exista (`AUTENTIFICARE_COD_ACTIVA`) — consumatorul e deja sub
 * acel flag.
 */
export function AlegereIntrare() {
  const { PATHS, PAGES } = useRoutesProvider();

  // Doua randuri: textul la stanga, butonul la dreapta, jumatate-jumatate (decizia owner-ului,
  // 21.09). Textul descrie butonul (`aria-describedby`), deci un cititor de ecran il spune odata cu el.
  // Ambele butoane pline, albastrul inchis al variantei implicite, cu hover-ul ei (tot 21.09).
  const intrari = [
    { id: 'intrare-autentificare', text: 'Ai deja cont?', buton: 'Autentificare', cale: PATHS[PAGES.LOGIN_EMAIL] },
    { id: 'intrare-inscriere', text: 'Ești profesor nou?', buton: 'Înscriere', cale: PATHS[PAGES.CERERE_COD] },
  ];

  return (
    <nav aria-label="Autentificare sau înscriere" className="grid grid-cols-1 gap-3">
      {intrari.map((i) => (
        <div key={i.id} className="grid grid-cols-2 items-center gap-3">
          <p id={i.id} className="m-0 text-left font-semibold text-dark">
            {i.text}
          </p>
          <Button asChild size="lg" className="w-full touch-target">
            <Link to={i.cale} aria-describedby={i.id}>{i.buton}</Link>
          </Button>
        </div>
      ))}
    </nav>
  );
}

/**
 * Sectiunea „Elevi” de sub „Profesori”: aceleasi doua randuri ca la profesori. Inscrierile elevilor
 * s-au deschis pe 2026-10-01 (decizia owner-ului; pana atunci butoanele erau dezactivate, cu toast).
 *
 * «Autentificare» duce la `/autentificare/email` (cod pentru un cont existent, valabil pentru orice
 * rol), «Înscrie-te» la `/elev/inregistrare` (cod de profesor + OTP pe email).
 * Doua LINKURI, ca la profesori: «Înapoi» din browser aduce omul aici.
 *
 * ⚠️ Textele spun „anul acesta” / „din anii trecuți” intentionat: login-ul elevului e scopat STRICT pe
 * editia curenta, deci un elev cu cont doar din editia trecuta nu primeste niciun cod pe
 * «Autentificare» (raspuns uniform, fara mesaj). El trebuie sa se inscrie din nou — primeste cont nou
 * in editia curenta. „Ai deja cont?” il trimitea exact pe calea care nu-i raspunde.
 */
export function AlegereElevi() {
  const { PATHS, PAGES } = useRoutesProvider();

  const intrari = [
    { id: 'elevi-autentificare', text: 'Ai cont de anul acesta?', buton: 'Autentificare', cale: PATHS[PAGES.LOGIN_EMAIL] },
    { id: 'elevi-inscriere', text: 'Nou sau din anii trecuți?', buton: 'Înscrie-te', cale: PATHS[PAGES.PUPIL_REGISTER] },
  ];

  return (
    <div role="group" aria-label="Elevi: autentificare sau înscriere" className="grid grid-cols-1 gap-3">
      {intrari.map((i) => (
        <div key={i.id} className="grid grid-cols-2 items-center gap-3">
          <p id={i.id} className="m-0 text-left font-semibold text-dark">
            {i.text}
          </p>
          <Button asChild size="lg" className="w-full touch-target">
            <Link
              to={i.cale}
              aria-describedby={i.id}
              onClick={() => {
                if (i.id === 'elevi-inscriere') trackSignupButtonClicked('elev', true);
              }}
            >
              {i.buton}
            </Link>
          </Button>
        </div>
      ))}
    </div>
  );
}

/**
 * «← Înapoi» sus pe ecranele de pas 2 (`/autentificare/email`, `/cod-de-acces`): duce la alegerea
 * de pe `/autentificare`. Link, nu `navigate(-1)`: cine a deschis pasul 2 direct (link din email,
 * semn de carte) n-are istoric in spate, iar un `-1` l-ar scoate din site.
 */
export function InapoiLaAlegere() {
  const { PATHS, PAGES } = useRoutesProvider();

  return (
    <Link
      to={PATHS[PAGES.LOGIN]}
      className="inline-flex min-h-11 items-center gap-1 self-start font-semibold text-magenta"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      Înapoi
    </Link>
  );
}
